/**
 * useTranslation Hook
 * 提供翻译功能和语言切换
 */

import { useCallback, useMemo } from 'react';
import { useLanguageStore } from '../stores/languageStore';
import { translations } from './locales';
import type { ActiveLanguage, TranslationParams, LanguageMetadata } from './types';
import { getActiveLanguages, isRTL } from './types';

/**
 * 获取嵌套翻译值
 * 支持点号分隔的路径，如 'common.action.confirm'
 */
function getTranslationValue(
  key: string,
  lang: ActiveLanguage
): string | undefined {
  const translation = translations[key];
  if (!translation) {
    console.warn(`[i18n] Missing translation key: "${key}"`);
    return undefined;
  }
  return translation[lang] ?? translation['en']; // fallback to English
}

/**
 * 替换模板变量
 * 支持 {variable} 和 {{variable}} 两种语法（locale 文件统一用 {x}）
 */
function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;

  return template.replace(/\{\{?(\w+)\}?\}/g, (match, key) => {
    const value = params[key];
    return value !== undefined ? String(value) : match;
  });
}

interface UseTranslationReturn {
  /** 当前语言 */
  language: ActiveLanguage;
  /** 切换语言 */
  setLanguage: (lang: ActiveLanguage) => void;
  /** 翻译函数 */
  t: (key: string, params?: TranslationParams) => string;
  /** 批量翻译（返回对象） */
  tBatch: <T extends Record<string, string>>(
    keys: T
  ) => { [K in keyof T]: string };
  /** 是否是 RTL 语言 */
  isRTL: boolean;
  /** 所有可用语言 */
  availableLanguages: LanguageMetadata[];
  /** 语言名称（本地语言） */
  languageName: string;
}

export function useTranslation(): UseTranslationReturn {
  const { language, setLanguage } = useLanguageStore();
  
  /**
   * 翻译函数
   * @param key 翻译键，如 'common.action.confirm' 或 'auth.login.title'
   * @param params 模板变量，如 { count: 5 }
   */
  const t = useCallback(
    (key: string, params?: TranslationParams): string => {
      const value = getTranslationValue(key, language);
      if (!value) {
        // 开发环境显示 key，生产环境返回空字符串
        return import.meta.env.DEV ? `[${key}]` : '';
      }
      return interpolate(value, params);
    },
    [language]
  );
  
  /**
   * 批量翻译
   * 用于一次翻译多个 key
   */
  const tBatch = useCallback(
    <T extends Record<string, string>>(keys: T): { [K in keyof T]: string } => {
      const result = {} as { [K in keyof T]: string };
      
      for (const [key, translationKey] of Object.entries(keys)) {
        result[key as keyof T] = t(translationKey);
      }
      
      return result;
    },
    [t]
  );
  
  /**
   * 当前语言的元数据
   */
  const languageMeta = useMemo(() => {
    return getActiveLanguages().find(l => l.code === language)!;
  }, [language]);
  
  return {
    language,
    setLanguage,
    t,
    tBatch,
    isRTL: isRTL(language),
    availableLanguages: getActiveLanguages(),
    languageName: languageMeta?.nativeName ?? language,
  };
}

/**
 * 简化版 hook，只返回 t 函数（性能优化）
 * 用于只需要翻译而不需要语言切换的场景
 */
export function useT(): (key: string, params?: TranslationParams) => string {
  const { language } = useLanguageStore();
  
  return useCallback(
    (key: string, params?: TranslationParams): string => {
      const value = getTranslationValue(key, language);
      if (!value) {
        return import.meta.env.DEV ? `[${key}]` : '';
      }
      return interpolate(value, params);
    },
    [language]
  );
}
